import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import colors from "../../../assets/theme/colors";
import { USER_ROLES } from "../../../constants/roles";

const DEFAULT_CAN_ITEMS = [
  "View medications",
  "Log medication doses",
  "Add daily care updates",
];

const SPECIALIST_CAN_ITEMS = [
  "View shared medication details",
  "Review care updates",
  "Add professional observations",
];

const getCanItemsForRole = (userRole) => (
  userRole === USER_ROLES.THERAPIST ? SPECIALIST_CAN_ITEMS : DEFAULT_CAN_ITEMS
);

const MedicationSetupRestrictedDialog = ({
  open,
  onClose,
  userRole,
  onContactCareOwner,
}) => {
  const canItems = getCanItemsForRole(userRole);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: colors.semantic.bg,
          border: `1px solid ${colors.semantic.profileFlow.cardBorder}`,
          boxShadow: `0 18px 48px ${alpha(colors.landing.deepNavy, 0.14)}`,
          m: { xs: 1.5, sm: 3 },
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 2.2, sm: 2.6 }, pb: 1 }}>
        <Stack spacing={1.8}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(colors.semantic.warning, 0.1),
              color: colors.semantic.warning,
            }}
          >
            <LockOutlinedIcon />
          </Box>

          <Box>
            <Typography
              variant="h6"
              sx={{
                color: colors.landing.heroText,
                fontWeight: 850,
                lineHeight: 1.12,
                mb: 0.8,
              }}
            >
              Medication Setup Restricted
            </Typography>
            <Typography sx={{ color: colors.landing.bodyText, lineHeight: 1.45 }}>
              Only the Care Owner or a Care Partner can add, edit, pause, or remove medication definitions.
            </Typography>
          </Box>

          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: colors.semantic.profileFlow.notStartedBg,
              border: `1px solid ${colors.semantic.profileFlow.cardBorder}`,
            }}
          >
            <Typography sx={{ color: colors.landing.heroText, fontWeight: 800, mb: 1 }}>
              You can still:
            </Typography>
            <Stack spacing={0.7}>
              {canItems.map((item) => (
                <Box key={item} sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                  <CheckCircleOutlineRoundedIcon sx={{ fontSize: 18, color: colors.semantic.sageDeep }} />
                  <Typography sx={{ color: colors.landing.bodyText, lineHeight: 1.3, fontSize: "0.94rem" }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          <Typography sx={{ color: colors.landing.textMuted, lineHeight: 1.45, fontSize: "0.93rem" }}>
            If medication details need to be changed, please contact the Care Owner or a Care Partner.
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2.2, sm: 2.6 }, pb: { xs: 2.2, sm: 2.6 }, pt: 1 }}>
        {typeof onContactCareOwner === "function" ? (
          <Button
            onClick={onContactCareOwner}
            sx={{
              textTransform: "none",
              color: colors.semantic.sageDeep,
              fontWeight: 800,
            }}
          >
            Contact Care Owner
          </Button>
        ) : null}
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            textTransform: "none",
            borderRadius: 1.5,
            bgcolor: colors.semantic.sageDeep,
            fontWeight: 850,
            boxShadow: "none",
            "&:hover": {
              bgcolor: colors.semantic.sageDeep,
              boxShadow: "none",
            },
          }}
        >
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MedicationSetupRestrictedDialog;
